import { ReactNode, useState, useCallback, useRef, useEffect } from "react"
import { RefreshCw } from "lucide-react"
import { cn } from "@/lib/utils"

interface PullToRefreshProps {
  children: ReactNode
  onRefresh: () => Promise<void>
  threshold?: number
  className?: string
}

export const PullToRefresh = ({ 
  children, 
  onRefresh, 
  threshold = 80,
  className 
}: PullToRefreshProps) => {
  const [isPulling, setIsPulling] = useState(false)
  const [pullDistance, setPullDistance] = useState(0)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const startY = useRef(0)
  const containerRef = useRef<HTMLDivElement>(null)

  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (window.scrollY === 0) {
      startY.current = e.touches[0].clientY
      setIsPulling(true)
    }
  }, [])

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isPulling) return

    const currentY = e.touches[0].clientY
    const distance = Math.max(0, currentY - startY.current)
    
    if (distance > 0) {
      e.preventDefault()
      setPullDistance(Math.min(distance * 0.5, threshold * 1.5))
    }
  }, [isPulling, threshold])

  const handleTouchEnd = useCallback(async () => {
    if (!isPulling) return

    setIsPulling(false)
    
    if (pullDistance >= threshold && !isRefreshing) {
      setIsRefreshing(true)
      try {
        await onRefresh()
      } finally {
        setIsRefreshing(false)
      }
    }
    
    setPullDistance(0)
  }, [isPulling, pullDistance, threshold, onRefresh, isRefreshing])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    container.addEventListener('touchstart', handleTouchStart, { passive: false })
    container.addEventListener('touchmove', handleTouchMove, { passive: false })
    container.addEventListener('touchend', handleTouchEnd)

    return () => {
      container.removeEventListener('touchstart', handleTouchStart)
      container.removeEventListener('touchmove', handleTouchMove)
      container.removeEventListener('touchend', handleTouchEnd)
    }
  }, [handleTouchStart, handleTouchMove, handleTouchEnd])

  const refreshProgress = Math.min(pullDistance / threshold, 1)
  const showRefreshIndicator = isPulling || isRefreshing

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      {/* Pull to refresh indicator */}
      <div 
        className={cn(
          "absolute top-0 left-0 right-0 flex items-center justify-center transition-all duration-300",
          "bg-background/95 backdrop-blur-lg border-b border-border/50",
          showRefreshIndicator ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0"
        )}
        style={{ height: Math.max(pullDistance, isRefreshing ? 60 : 0) }}
      >
        <div className="flex items-center space-x-2 text-muted-foreground">
          <RefreshCw 
            className={cn(
              "h-5 w-5 transition-transform duration-300",
              isRefreshing ? "animate-spin" : "",
              `rotate-${Math.floor(refreshProgress * 360)}`
            )}
            style={{ 
              transform: `rotate(${refreshProgress * 360}deg)`,
              color: refreshProgress >= 1 ? 'hsl(var(--primary))' : undefined
            }}
          />
          <span className="text-sm font-medium">
            {isRefreshing 
              ? "Refreshing..." 
              : refreshProgress >= 1 
                ? "Release to refresh" 
                : "Pull to refresh"
            }
          </span>
        </div>
      </div>

      {/* Content */}
      <div 
        style={{ 
          transform: `translateY(${showRefreshIndicator ? Math.max(pullDistance, 60) : 0}px)`,
          transition: isPulling ? 'none' : 'transform 300ms ease-out'
        }}
      >
        {children}
      </div>
    </div>
  )
}